// api.js

const generatePassword = () => Math.random().toString(36).slice(-10) + '!Ab1';
const authHeader = `Basic ${btoa(`maolaraba:7N3t nbiK di03 rEWo cj5t D7Tu`)}`;
const siteUrl = 'https://triteklibrary.co.uk';

// ─── TritekConnect invite ─────────────────────────────────────────────────────
// Fire-and-forget — never throws, never blocks payment flow.
// Replace the URL with your actual Next.js app domain.

// Must be the canonical www host: the apex 307-redirects to it, and a CORS
// preflight is not allowed to follow redirects.
const TRITEKCONNECT_URL = 'https://www.tritekconnect.com';

export const sendTritekConnectInvite = async (email) => {
  try {
    const res = await fetch(`${TRITEKCONNECT_URL}/api/external/invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!res.ok) {
      const body = await res.text();
      // 409 = already invited — not an error worth logging loudly
      if (res.status === 409) {
        console.info(`[tritekconnect] ${email} already has a pending invite`);
        return { ok: true, already: true };
      }
      console.warn(`[tritekconnect] Invite failed for ${email}: ${res.status} ${body}`);
      return { ok: false, error: `${res.status} ${body}`.trim().slice(0, 200) };
    }
    console.info(`[tritekconnect] Invite sent to ${email}`);
    return { ok: true };
  } catch (err) {
    // Network error, timeout, etc. — swallow it completely
    console.warn(`[tritekconnect] Invite request error for ${email}:`, err);
    return { ok: false, error: err?.message || String(err) };
  }
};

// ─── Payment notification to the office ────────────────────────────────────────
// What payments@ needs to see about a completed payment. Built apart from the
// request so it can be checked without a network.
export const paymentNotification = (userDetails = {}, paymentData = {}, result = {}) => ({
  email: (userDetails.email || '').trim().toLowerCase(),
  name: `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim(),
  phone: userDetails.phone || '',
  // courseName is the readable title; courseKey is the code the checkout uses.
  course: paymentData.courseName || paymentData.courseKey || '',
  amountMinor: Number(paymentData.amountPaid) || 0,
  currency: paymentData.currency || 'GBP',
  method: paymentData.source || '',
  referralCode: (paymentData.referralCode || '').trim(),
  staff: paymentData.staff || '',
  wpUserId: result?.user?.id ?? '',
  wooOrderId: result?.order?.id ?? result?.order?.order_id ?? '',
});

// Fire-and-forget: a missed alert must never fail a payment already taken.
export const notifyPaymentReceived = async (userDetails, paymentData, result) => {
  try {
    const res = await fetch(`${TRITEKCONNECT_URL}/api/external/payment-received`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(paymentNotification(userDetails, paymentData, result)),
    });
    if (!res.ok) console.warn('[payments] alert failed:', res.status, await res.text());
    return { ok: res.ok };
  } catch (err) {
    console.warn('[payments] alert request error:', err);
    return { ok: false, error: err?.message || String(err) };
  }
};

// Healthcare-analysis buyers are excluded from bulk TritekConnect invites.
// Covers the standalone course ("Health Analyitcs" — the typo is the stored
// value) and the combo codes that include it.
const HEALTHCARE_CODES = new Set(["pmha", "daha", "baha"]);

export const isHealthcareCourse = (productName) => {
  const n = String(productName || "").trim().toLowerCase();
  return n.includes("health") || HEALTHCARE_CODES.has(n);
};

// Who a bulk invite actually goes to: one send per email address, healthcare
// orders dropped. An email that also has a non-healthcare order still gets it.
export const inviteRecipients = (orders) => {
  const seen = new Set();
  return orders.filter((order) => {
    const email = (order.email || "").trim().toLowerCase();
    if (!email || seen.has(email) || isHealthcareCourse(order.productName)) return false;
    seen.add(email);
    return true;
  });
};

const API_BASE = 'https://innovators.onrender.com';

// ─── Matching a ?course= parameter to a course ────────────────────────────────
// Separators are ignored on both sides. Links are written by people, and a
// combined course has no agreed punctuation: PMBACS, PMBA-CS, "PMBA + CS" and
// "pmba cs" are plainly the same thing. A link that quietly fell back to the
// default course because of a dash was a way to sell somebody the wrong one.
export const normalizeCourseKey = (s) =>
  String(s || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");

export const matchCourse = (courseOptions, raw) => {
  const target = normalizeCourseKey(raw);
  if (!target) return null;

  for (const key of Object.keys(courseOptions || {})) {
    if (normalizeCourseKey(key) === target) return key;
  }
  for (const [key, cfg] of Object.entries(courseOptions || {})) {
    if (cfg?.aliases?.some((a) => normalizeCourseKey(a) === target)) return key;
  }
  return null;
};

// ─── Card payment with 3D Secure ──────────────────────────────────────────────
// The server creates the PaymentIntent on-session and asks the issuer for 3DS.
// If the issuer challenges, it hands back a client secret instead of a result:
// the customer authenticates here, then the server re-reads the intent from
// Stripe and records it. Nothing is written until the money actually moves.
//
// Throws on failure so callers fall into their existing catch.
export const payWithCard = async ({ stripe, endpoint, payload }) => {
  const post = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || 'Payment failed. Please try again.');
    return data;
  };

  const data = await post(endpoint, payload);
  if (!data?.requiresAction) return data;

  // 3DS challenge — Stripe.js shows it in-page.
  const { error, paymentIntent } = await stripe.handleNextAction({ clientSecret: data.clientSecret });
  if (error) throw error;

  return post(`${API_BASE}/finalize-card-payment`, { paymentIntentId: paymentIntent.id });
};

// ─── Why a payment did not go through ─────────────────────────────────────────
// Klarna and Clearpay decline on their own credit assessment, and the reason
// never reaches the browser — Stripe just reports the attempt failed. Showing
// one generic message for every cause is why declined candidates retry the
// same provider over and over instead of switching method, so name who said no
// and where to go next.
const PROVIDER_LABEL = {
  klarna: "Klarna",
  clearpay: "Clearpay",
  afterpay_clearpay: "Clearpay",
  paypal: "PayPal",
  super: "Super",
  "super-paylater": "Super",
};

export const paymentFailureMessage = (method, reason = "", fallback = "") => {
  const provider = PROVIDER_LABEL[String(method || "").toLowerCase()];
  const r = String(reason || "").toLowerCase();

  const f = String(fallback || "");
  // Our own 409 guard, not a decline — saying "Klarna declined you" here is a lie.
  if (/duplicate/i.test(f) || /duplicate/i.test(r)) return "That looked like a double click. Wait a few seconds and try again.";
  // axios boilerplate is not worth showing anyone
  const usable = /^Request failed with status code/.test(f) ? "" : f;

  if (r.includes("expired") || r.includes("timed_out")) return "Session expired. Try again or pay by card.";
  if (r.includes("cancel") || r.includes("abandon")) return "Payment cancelled. Nothing was charged.";
  if (provider && provider !== "PayPal") return `${provider} declined this payment. Nothing was charged — try card or bank transfer.`;
  return usable || "Payment failed. Try another payment method.";
};

// ─── Super Payments status mapping ────────────────────────────────────────────
// Super's transaction statuses, folded into the vocabulary the confirmation page
// already branches on. Anything that is not success or still-in-flight is
// treated as not paid — no access is granted on an ambiguous status.
export const superStatusToLocal = (transactionStatus) =>
  transactionStatus === 'PaymentSuccess'
    ? 'succeeded'
    : transactionStatus === 'PaymentPending' || transactionStatus === 'PaymentDelayed'
    ? 'processing'
    : 'canceled';

// ─────────────────────────────────────────────────────────────────────────────

const checkUserExists = async (email) => {
  const res = await fetch(`${siteUrl}/wp-json/wp/v2/users?search=${email}`, {
    headers: { Authorization: authHeader },
  });
  const users = await res.json();
  return users.length > 0 ? users[0] : null;
};

// Create the WordPress user account and then create a WooCommerce order.
export const createUserAccount = async (userDetails, paymentData) => {
  try {
    // 1) Find or create the user
    let user = await checkUserExists(userDetails.email);

    if (!user) {
      const password = generatePassword();

      const resp = await fetch(
        'https://triteklibrary.co.uk/wp-json/custom/v1/create-user',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: userDetails.email,
            password,
            first_name: userDetails.firstName,
            last_name: userDetails.lastName,
            phone: userDetails.phone ?? '',
            referral_code_used: (paymentData?.referralCode || '').trim(),
          }),
        }
      );

      const data = await resp.json();
      if (!resp.ok) {
        // WordPress puts REST errors in `message` (with a `code`); our own PHP
        // puts them in `error`. Read both, or a real cause gets swallowed.
        const why = data?.error || data?.message || 'Failed to create user';
        throw new Error(data?.code ? `${why} (${data.code})` : why);
      }
      user = { ...data, id: data?.user_id };
      if (!user?.id) throw new Error('User created but no ID returned');
    }

    const amountPaid = (paymentData.amountPaid / 100).toFixed(2);

    // 2) Create WooCommerce order
    const orderPayload = {
      name: `${userDetails.firstName} ${userDetails.lastName}`.trim(),
      email: userDetails.email,
      phone: userDetails.phone ?? '',
      amountPaid,
      product_id: paymentData.productId,
      currency: paymentData.currency,
      customer_id: user.id,
      referralCode: (paymentData?.referralCode || '').trim(),
      courseName: paymentData.courseName || paymentData.courseKey || '',
      courseKey: paymentData.courseKey || '',
    };
    const order = await createOrderInWooCommerce(orderPayload);

    // 3) Record affiliate conversion (if referral code provided)
    const ref = (paymentData?.referralCode || '').trim();
    if (ref) {
      try {
        await fetch('https://innovators.onrender.com/affiliate/record', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            referralCode: ref,
            referredUserId: user.id,
            referredEmail: userDetails.email,
            wcOrderId: order?.id || order?.order_id || null,
            amount: Number(amountPaid),
            currency: paymentData.currency || 'GBP',
            productId: paymentData.productId || null,
            courseKey: paymentData.courseKey || '',
            source: 'unifiedpay',
            createdAtISO: new Date().toISOString(),
          }),
        });
      } catch (e) {
        console.warn('Affiliate conversion record failed:', e);
      }
    }

    // 4) Invite user to TritekConnect workspace — fire and forget, never blocks
    sendTritekConnectInvite(userDetails.email);

    // 5) Tell payments@. Skipped for admin repairs and bulk imports, which
    // re-run this for access that was paid for long ago.
    if (!paymentData?.skipEmail) {
      notifyPaymentReceived(userDetails, paymentData, { user, order });
    }

    return { success: true, data: { user, order } };
  } catch (error) {
    console.error('Error creating user/order:', error);
    return { success: false, error: error.message || String(error) };
  }
};

// Create an order in WooCommerce.
export const createOrderInWooCommerce = async (orderDetails) => {
  const response = await fetch('https://innovators.onrender.com/create-order', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(orderDetails),
  });

  // Always read as text first — the backend or WordPress may return HTML
  // (a fatal-error page, a proxy error, etc.) instead of JSON, and calling
  // response.json() directly on that throws a SyntaxError that hides the
  // real error message behind "Unexpected token '<'".
  const rawText = await response.text();

  if (!response.ok) {
    let message = rawText;
    try {
      const parsed = JSON.parse(rawText);
      message = parsed.error || parsed.message || rawText;
    } catch {
      // Not JSON — keep rawText as-is (likely an HTML error page).
      // Trim it down so the thrown error is still readable.
      message = rawText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 500);
    }
    throw new Error(message || 'Order creation failed');
  }

  try {
    return JSON.parse(rawText);
  } catch {
    throw new Error('Order created but response was not valid JSON: ' + rawText.slice(0, 300));
  }
};

export const verifyKlarnaSession = async (orderId, expectedAmount) => {
  try {
    const resp = await fetch(
      'https://innovators.onrender.com/api/verify-klarna-session',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          expectedAmount ? { orderId, expectedAmount } : { orderId }
        ),
      }
    );

    const data = await resp.json();

    if (!resp.ok) {
      throw new Error(data?.error || data?.message || 'Verification failed');
    }

    if (!data?.success) {
      return { success: false, error: data?.error || 'Payment not authorized/captured' };
    }

    return {
      success: true,
      status: data.status,
      fraud_status: data.fraud_status,
      order_amount: data.order_amount,
      currency: data.currency,
      captured_amount: data.captured_amount,
      remaining_authorized_amount: data.remaining_authorized_amount,
    };
  } catch (err) {
    console.error('Error verifying Klarna session:', err);
    return { success: false, error: err.message || 'Verification failed' };
  }
};